class ImageUploadFields extends HTMLElement {
  connectedCallback() {
    this.maxFields = parseInt(this.dataset.maxFields || '5', 10);
    this.maxFileSize = 5 * 1024 * 1024;
    this.list = this.querySelector('[data-upload-list]');
    this.form = this.closest('form');

    if (!this.list) return;

    this.setupFieldPreviews(this);
    this.ensureOneEmptyFieldAtEnd();
    this.updateDynamicCheckoutState();
    this.bindSubmitSanitizer();
    this.observeDynamicCheckout();
  }

  get fieldCount() {
    return this.list.querySelectorAll('input[type="file"]').length;
  }

  get selectedFileCount() {
    return Array.from(this.list.querySelectorAll('input[type="file"]')).filter((input) => input.files && input.files.length > 0).length;
  }

  addField() {
    if (this.fieldCount >= this.maxFields) return;

    const index = this.fieldCount + 1;
    const inputId = `UploadedImageDynamic-${Date.now()}-${index}`;
    const field = document.createElement('div');
    field.className = 'field';
      field.innerHTML = `
      <div class="product-image-upload__field grid gap-2" data-upload-field>
        <label class="label">Image ${index}</label>
        <input class="sr-only" id="${inputId}" type="file" name="properties[Uploaded image ${index}]" accept="image/*" data-upload-input />
        <label class="button button--secondary product-image-upload__trigger" for="${inputId}">CHOOSE IMAGE</label>
        <div class="product-image-upload__preview-wrap" data-upload-preview-wrap hidden>
          <img class="product-image-upload__preview" data-upload-preview alt="Selected upload preview" />
          <button class="product-image-upload__remove" type="button" data-upload-remove aria-label="Remove selected image">×</button>
        </div>
        <p class="product-image-upload__error text-xs" data-upload-error hidden></p>
      </div>
    `;
    this.list.appendChild(field);
    this.setupFieldPreviews(field);
    this.updateLabels();
  }

  updateLabels() {
    const fields = this.list.querySelectorAll('[data-upload-field]');
    fields.forEach((field, index) => {
      const label = field.querySelector('.label');
      const input = field.querySelector('input[type="file"]');
      const inputId = input?.id;
      if (label) {
        label.textContent = `Image ${index + 1}`;
      }
      if (input) {
        input.name = `properties[Uploaded image ${index + 1}]`;
      }
      const trigger = field.querySelector('.product-image-upload__trigger');
      if (trigger && inputId) {
        trigger.setAttribute('for', inputId);
      }
    });
  }

  ensureOneEmptyFieldAtEnd() {
    const fields = Array.from(this.list.querySelectorAll('[data-upload-field]'));
    const selectedFields = fields.filter((field) => {
      const input = field.querySelector('input[type="file"]');
      return input && input.files && input.files.length > 0;
    });

    const hasEmptyField = fields.some((field) => {
      const input = field.querySelector('input[type="file"]');
      return input && (!input.files || input.files.length === 0);
    });

    if (!hasEmptyField && fields.length < this.maxFields) {
      this.addField();
      return;
    }

    // Keep only one trailing empty field.
    let emptyFound = false;
    fields.forEach((field) => {
      const input = field.querySelector('input[type="file"]');
      const isEmpty = input && (!input.files || input.files.length === 0);
      if (!isEmpty) return;
      if (!emptyFound) {
        emptyFound = true;
      } else {
        field.closest('.field')?.remove();
      }
    });

    // If nothing selected, keep only first empty field.
    if (selectedFields.length === 0) {
      const allFields = Array.from(this.list.querySelectorAll('.field'));
      allFields.forEach((field, index) => {
        if (index > 0) field.remove();
      });
    }

    this.updateLabels();
    this.updateDynamicCheckoutState();
  }

  updateDynamicCheckoutState() {
    if (!this.form) return;

    const dynamicCheckouts = Array.from(this.form.querySelectorAll('.shopify-payment-button'));
    if (dynamicCheckouts.length === 0) {
      return;
    }

    const hasUploads = this.selectedFileCount > 0;
    dynamicCheckouts.forEach((dynamicCheckout) => {
      dynamicCheckout.hidden = hasUploads;
      dynamicCheckout.setAttribute('aria-hidden', hasUploads ? 'true' : 'false');
    });
  }

  bindSubmitSanitizer() {
    if (!this.form || this.form.dataset.uploadSanitizerBound === 'true') return;
    this.form.dataset.uploadSanitizerBound = 'true';

    this.form.addEventListener('submit', () => {
      this.removeSanitizedUploadFields();

      const inputs = Array.from(this.list.querySelectorAll('[data-upload-input]'));
      let index = 1;
      inputs.forEach((input) => {
        const file = input.files && input.files[0];
        if (!file) return;

        // Prevent browser path placeholders such as C:\fakepath\...
        input.dataset.originalName = input.name || '';
        input.removeAttribute('name');

        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = `properties[Uploaded image ${index}]`;
        hidden.value = file.name;
        hidden.setAttribute('data-upload-sanitized-field', 'true');
        this.form.appendChild(hidden);
        index += 1;
      });
    });
  }

  removeSanitizedUploadFields() {
    if (!this.form) return;
    this.form.querySelectorAll('[data-upload-sanitized-field="true"]').forEach((node) => node.remove());
    this.list.querySelectorAll('[data-upload-input]').forEach((input) => {
      if (input.dataset.originalName) {
        input.name = input.dataset.originalName;
        delete input.dataset.originalName;
      }
    });
  }

  observeDynamicCheckout() {
    if (!this.form || this.form.dataset.uploadDynamicObserverBound === 'true') return;
    this.form.dataset.uploadDynamicObserverBound = 'true';

    const observer = new MutationObserver(() => {
      this.updateDynamicCheckoutState();
    });

    observer.observe(this.form, { childList: true, subtree: true });
  }

  setupFieldPreviews(scope) {
    const fields = scope.querySelectorAll('[data-upload-field]');
    fields.forEach((field) => {
      if (field.dataset.previewBound === 'true') return;
      field.dataset.previewBound = 'true';

      const input = field.querySelector('[data-upload-input]');
      const preview = field.querySelector('[data-upload-preview]');
      const previewWrap = field.querySelector('[data-upload-preview-wrap]');
      const removeButton = field.querySelector('[data-upload-remove]');
      const error = field.querySelector('[data-upload-error]');
      if (!input || !preview || !previewWrap || !removeButton || !error) return;

      const updatePreview = () => {
        error.hidden = true;
        error.textContent = '';

        if (preview.dataset.objectUrl) {
          URL.revokeObjectURL(preview.dataset.objectUrl);
          delete preview.dataset.objectUrl;
        }

        const file = input.files && input.files[0];
        if (!file) {
          previewWrap.hidden = true;
          preview.removeAttribute('src');
          field.classList.remove('has-preview');
          this.ensureOneEmptyFieldAtEnd();
          return;
        }

        const isImage = typeof file.type === 'string' && file.type.startsWith('image/');
        if (!isImage) {
          input.value = '';
          previewWrap.hidden = true;
          preview.removeAttribute('src');
          field.classList.remove('has-preview');
          error.textContent = 'Please upload an image file only.';
          error.hidden = false;
          return;
        }

        if (file.size > this.maxFileSize) {
          input.value = '';
          previewWrap.hidden = true;
          preview.removeAttribute('src');
          field.classList.remove('has-preview');
          error.textContent = 'Image must be smaller than 5MB.';
          error.hidden = false;
          return;
        }

        const objectUrl = URL.createObjectURL(file);
        preview.src = objectUrl;
        preview.dataset.objectUrl = objectUrl;
        previewWrap.hidden = false;
        field.classList.add('has-preview');
        this.ensureOneEmptyFieldAtEnd();
      };

      input.addEventListener('change', updatePreview);
      preview.addEventListener('click', () => input.click());
      removeButton.addEventListener('click', (event) => {
        event.preventDefault();
        error.hidden = true;
        error.textContent = '';
        if (preview.dataset.objectUrl) {
          URL.revokeObjectURL(preview.dataset.objectUrl);
          delete preview.dataset.objectUrl;
        }
        field.closest('.field')?.remove();
        this.ensureOneEmptyFieldAtEnd();
      });
    });
  }
}

if (!customElements.get('image-upload-fields')) {
  customElements.define('image-upload-fields', ImageUploadFields);
}
